import { useTraderCommissionStats } from '../hooks/useTraderCommissionStats';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Link, useLocation } from 'react-router-dom';
import {
    ChevronDown,
    ChevronRight,
    Users,
    Settings,
    CreditCard,
    Wallet,
    DollarSign,
    RefreshCw,
    TrendingUp,
    TrendingDown,
    ArrowRightLeft,
    List,
    AlertCircle
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { traderAPI, traderAuthAPI, TraderSettings, paymentsAPI } from '../lib/api';
import { balanceEvents, BALANCE_EVENTS } from '../lib/events';
import { formatAmountWithSeparator } from '../lib/utils';
import { TRADER_BALANCE_REFRESH_INTERVAL_MS } from '../lib/polling';
import { cabinetPath } from '../lib/domains';

interface MenuSection {
    id: string;
    title: string;
    icon: React.ReactNode;
    items: MenuItem[];
    isCollapsible?: boolean;
    defaultExpanded?: boolean;
}

interface MenuItem {
    id: string;
    title: string;
    path: string;
    count?: number;
    badge?: string;
}

export const Sidebar: React.FC = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(['deposit', 'withdraw', 'operations']) // По умолчанию раскрыты все разделы
    );
    const [traderSettings, setTraderSettings] = useState<TraderSettings | null>(null);
    const commissionStats = useTraderCommissionStats();
    const [activePaymentsCount, setActivePaymentsCount] = useState({ deposit: 0, withdraw: 0 });
    const [appealsCount, setAppealsCount] = useState<number>(0);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const loadingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const isLoadingSettingsRef = React.useRef(false);

    const exchangeRate = traderSettings?.exchange_rate ?? null;
    const fiatCurrency = traderSettings?.fiat_currency ?? 'RUB';


    const toggleSection = (sectionId: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionId)) {
            newExpanded.delete(sectionId);
        } else {
            newExpanded.add(sectionId);
        }
        setExpandedSections(newExpanded);
    };

    // Загрузка настроек трейдера (включая баланс) с debounce
    const loadTraderSettings = React.useCallback(async (immediate = false) => {
        if (!user) return;

        if (isLoadingSettingsRef.current && !immediate) return;

        // Очищаем предыдущий таймаут
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }

        const doLoad = async () => {
            try {
                isLoadingSettingsRef.current = true;

                // Показываем индикатор загрузки только при первой загрузке
                if (immediate && !traderSettings) {
                    setLoadingBalance(true);
                }

                const [settings, depositPaymentsResponse, withdrawPaymentsResponse, appealsCountResponse] = await Promise.all([
                    traderAPI.getSettings(),
                    paymentsAPI.getTraderActivePayments(1, 1, 'deposit'),
                    paymentsAPI.getTraderActivePayments(1, 1, 'withdraw'),
                    paymentsAPI.getTraderAppealsCount()
                ]);

                // Обновляем только если данные изменились
                setTraderSettings(prevSettings => {
                    const dataString = JSON.stringify(settings);
                    const prevString = JSON.stringify(prevSettings);
                    return dataString === prevString ? prevSettings : settings;
                });

                // Обновляем количество активных платежей из total
                setActivePaymentsCount(previous =>
                    previous.deposit === depositPaymentsResponse.total && previous.withdraw === withdrawPaymentsResponse.total
                        ? previous
                        : { deposit: depositPaymentsResponse.total, withdraw: withdrawPaymentsResponse.total });

                setAppealsCount(prevCount => {
                    if (prevCount === appealsCountResponse.count) {
                        return prevCount;
                    }
                    return appealsCountResponse.count;
                });

            } catch (error) {
                console.error('Ошибка загрузки настроек трейдера:', error);
            } finally {
                isLoadingSettingsRef.current = false;
                if (immediate && !traderSettings) {
                    setLoadingBalance(false);
                }
            }
        };

        // Если immediate - загружаем сразу, иначе с задержкой 300мс
        if (immediate) {
            await doLoad();
        } else {
            loadingTimeoutRef.current = setTimeout(doLoad, 300);
        }
    }, [user, traderSettings]);

    // Загружаем настройки при монтировании компонента
    useEffect(() => {
        if (user) {
            loadTraderSettings(true); // Immediate load при первой загрузке
        }
    }, [loadTraderSettings]);

    // Автоматическое фоновое обновление баланса
    useEffect(() => {
        if (!user) return;

        const interval = setInterval(() => {
            loadTraderSettings(true); // Immediate для периодического обновления
        }, TRADER_BALANCE_REFRESH_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [loadTraderSettings]);


    // Подписка на события обновления баланса (без обновления при смене маршрута)
    useEffect(() => {
        if (!user) return;

        const handleBalanceUpdate = () => {
            loadTraderSettings(); // С debounce для событий
        };

        // Подписываемся на все события, связанные с балансом
        balanceEvents.on(BALANCE_EVENTS.UPDATED, handleBalanceUpdate);
        balanceEvents.on(BALANCE_EVENTS.SETTINGS_CHANGED, handleBalanceUpdate);
        balanceEvents.on(BALANCE_EVENTS.PAYMENT_CONFIRMED, handleBalanceUpdate);
        balanceEvents.on(BALANCE_EVENTS.ORDER_CREATED, handleBalanceUpdate);
        balanceEvents.on(BALANCE_EVENTS.DEPOSIT_COMPLETED, handleBalanceUpdate);

        return () => {
            balanceEvents.off(BALANCE_EVENTS.UPDATED, handleBalanceUpdate);
            balanceEvents.off(BALANCE_EVENTS.SETTINGS_CHANGED, handleBalanceUpdate);
            balanceEvents.off(BALANCE_EVENTS.PAYMENT_CONFIRMED, handleBalanceUpdate);
            balanceEvents.off(BALANCE_EVENTS.ORDER_CREATED, handleBalanceUpdate);
            balanceEvents.off(BALANCE_EVENTS.DEPOSIT_COMPLETED, handleBalanceUpdate);
        };
    }, [user, loadTraderSettings]);

    // Cleanup при размонтировании
    useEffect(() => {
        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, []);

    const menuSections: MenuSection[] = [
        {
            id: 'deposit',
            title: 'Пополнение',
            icon: <TrendingUp className="h-4 w-4" />,
            isCollapsible: true,
            defaultExpanded: true,
            items: [
                { id: 'deposit-working', title: 'Рабочие реквизиты', path: cabinetPath('trader', '/dashboard?tab=deposit-working') },
                { id: 'deposit-archive', title: 'Архив', path: cabinetPath('trader', '/dashboard?tab=deposit-archive') },
            ]
        },
        {
            id: 'withdraw',
            title: 'Вывод',
            icon: <TrendingDown className="h-4 w-4" />,
            isCollapsible: true,
            defaultExpanded: true,
            items: [
                { id: 'withdraw-working', title: 'Рабочие реквизиты', path: cabinetPath('trader', '/dashboard?tab=withdraw-working') },
                { id: 'withdraw-archive', title: 'Архив', path: cabinetPath('trader', '/dashboard?tab=withdraw-archive') },
            ]
        },
        {
            id: 'operations',
            title: 'Операции',
            icon: <Wallet className="h-4 w-4" />,
            isCollapsible: true,
            defaultExpanded: true,
            items: [
                {
                    id: 'active-deposit',
                    title: 'Пополнение',
                    path: cabinetPath('trader', '/dashboard?tab=active-deposit'),
                    count: activePaymentsCount.deposit || undefined
                },
                {
                    id: 'active-withdraw',
                    title: 'Вывод',
                    path: cabinetPath('trader', '/dashboard?tab=active-withdraw'),
                    count: activePaymentsCount.withdraw || undefined
                },
                { id: 'all-requisites', title: 'Все рабочие реквизиты', path: cabinetPath('trader', '/dashboard?tab=all-requisites') },
                { id: 'archive', title: 'История', path: cabinetPath('trader', '/dashboard?tab=history') },
                { id: 'appeals', title: 'Апелляции', path: cabinetPath('trader', '/appeals'), count: appealsCount > 0 ? appealsCount : undefined },
                { id: 'bank-statements', title: 'Банковские выписки', path: cabinetPath('trader', '/dashboard?tab=bank-statements') },
            ]
        },
        {
            id: 'settings',
            title: 'Настройки',
            icon: <Settings className="h-4 w-4" />,
            isCollapsible: false,
            items: [
                { id: 'trader-settings', title: 'Настройки трейдера', path: cabinetPath('trader', '/dashboard?tab=settings') },
                { id: 'automation', title: 'Автоматика', path: cabinetPath('trader', '/dashboard?tab=automation') },
                { id: 'notifications', title: 'Пуши', path: cabinetPath('trader', '/dashboard?tab=notifications') },
            ]
        }
    ];

    // Автоматическое раскрытие секций при навигации
    useEffect(() => {
        const currentPath = location.pathname + location.search;

        // Находим секцию, которая содержит активный элемент
        const activeSection = menuSections.find(section =>
            section.items.some(item => {
                if (item.path.includes('?tab=')) {
                    const tab = item.path.split('?tab=')[1];
                    // Проверяем активный tab или дефолтный (deposit-working) для /trader/dashboard
                    return new URLSearchParams(location.search).get('tab') === tab ||
                        (location.pathname === cabinetPath('trader', '/dashboard') && !location.search && tab === 'deposit-working');
                }
                return item.path === currentPath;
            })
        );

        // Если найдена активная секция и она сворачиваемая, раскрываем её
        if (activeSection && activeSection.isCollapsible) {
            setExpandedSections(prev => {
                const newSet = new Set(prev);
                newSet.add(activeSection.id);
                return newSet;
            });
        }
    }, [location.pathname, location.search]);

    const isActiveItem = (path: string) => {
        if (path.includes('?tab=')) {
            const tab = path.split('?tab=')[1];
            return new URLSearchParams(location.search).get('tab') === tab ||
                (location.pathname === cabinetPath('trader', '/dashboard') && !location.search && tab === 'deposit-working');
        }
        return location.pathname === path;
    };

    const hasActiveItem = (section: MenuSection) => {
        return section.items.some(item => isActiveItem(item.path));
    };

    return (
        <div className="w-64 bg-gray-900 text-white min-h-screen flex flex-col relative">
            {/* Header */}
            <div className="p-4 border-b border-gray-700">
                <Link to={cabinetPath('trader', '/dashboard')} className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Wallet className="h-5 w-5" />
                    </div>
                    <span className="font-bold text-lg">Payment System</span>
                </Link>
            </div>

            {/* Exchange Rate Section */}
            <div className="p-4 border-b border-gray-700 bg-gradient-to-r from-blue-900 to-blue-800">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <ArrowRightLeft className="h-4 w-4 text-blue-300" />
                        <span className="text-sm font-medium text-blue-100">
                            Курс USDT/{fiatCurrency}
                        </span>
                    </div>
                </div>
                {exchangeRate ? (
                    <div className="space-y-1">
                        <div className="text-xl font-bold text-white">
                            1 USDT = {exchangeRate.toFixed(2)} {fiatCurrency === 'RUB' ? '₽' : fiatCurrency}
                        </div>
                    </div>
                ) : loadingBalance ? (
                    <div className="text-blue-200 text-sm">Загрузка...</div>
                ) : (
                    <div className="text-blue-200 text-sm">Не удалось загрузить</div>
                )}
            </div>

            {/* Balance Section */}
            {user && (
                <div className="p-4 border-b border-gray-700 bg-gray-800">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <DollarSign className="h-5 w-5 text-green-400" />
                            <span className="font-medium text-gray-300">Баланс трейдера</span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadTraderSettings(true)}
                            disabled={loadingBalance}
                            className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                        >
                            <RefreshCw className={`h-3 w-3 ${loadingBalance ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                    {traderSettings ? (
                        <div className="space-y-2">
                            <div className="text-lg font-bold text-white">
                                {formatAmountWithSeparator(traderSettings.balance, 'USDT')}
                            </div>

                            {/* Заработок за сегодня */}
                            <div className="text-sm text-green-400" title="Новый день начинается в 00:00 по вашему времени">
                                💰 Заработано сегодня: {commissionStats ? formatAmountWithSeparator(commissionStats.today_commission_earned, 'USDT') : '—'}
                            </div>

                            <div className="space-y-1">
                                {traderSettings.insurance_balance !== undefined && (
                                    <div className="text-xs text-blue-300">
                                        Страховой баланс: {formatAmountWithSeparator(traderSettings.insurance_balance, 'USDT')}
                                    </div>
                                )}
                                {(traderSettings.frozen_balance ?? 0) > 0 && (
                                    <div className="text-xs text-yellow-400">
                                        Заморожено (споры): {formatAmountWithSeparator(traderSettings.frozen_balance ?? 0, 'USDT')}
                                    </div>
                                )}
                            </div>

                            {/* Кнопка пополнения баланса */}
                            <div className="pt-2">
                                <Link to={cabinetPath('trader', '/dashboard?tab=deposits')}>
                                    <Button
                                        size="sm"
                                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                                    >
                                        <Wallet className="h-3 w-3 mr-1" />
                                        Пополнить баланс
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    ) : loadingBalance ? (
                        <div className="text-gray-400 text-sm">Загрузка...</div>
                    ) : (
                        <div className="text-gray-400 text-sm">Не удалось загрузить</div>
                    )}
                </div>
            )}

            {/* Menu Sections */}
            <div className="flex-1 overflow-y-auto pb-20">
                {menuSections.map((section) => (
                    <div key={section.id} className="border-b border-gray-700">
                        {section.isCollapsible ? (
                            <Button
                                variant="ghost"
                                className={`w-full justify-between p-4 h-auto text-left hover:bg-gray-800 ${hasActiveItem(section) ? 'text-blue-400 bg-gray-800' : 'text-white'
                                    }`}
                                onClick={() => toggleSection(section.id)}
                            >
                                <div className="flex items-center gap-3">
                                    {section.icon}
                                    <span className="font-medium">{section.title}</span>
                                </div>
                                {expandedSections.has(section.id) ? (
                                    <ChevronDown className="h-4 w-4" />
                                ) : (
                                    <ChevronRight className="h-4 w-4" />
                                )}
                            </Button>
                        ) : (
                            <div className="p-4">
                                <div className={`flex items-center gap-3 font-medium ${hasActiveItem(section) ? 'text-blue-400' : 'text-white'
                                    }`}>
                                    {section.icon}
                                    <span>{section.title}</span>
                                </div>
                            </div>
                        )}

                        {(expandedSections.has(section.id) || !section.isCollapsible) && (
                            <div className="pb-2">
                                {section.items.map((item) => (
                                    <Link
                                        key={item.id}
                                        to={item.path}
                                        className={`
                                            flex items-center justify-between px-6 py-2 text-sm hover:bg-gray-800 transition-colors
                                            ${isActiveItem(item.path) ? 'bg-blue-600 text-white' : 'text-gray-300'}
                                        `}
                                    >
                                        <span>{item.title}</span>
                                        {item.count !== undefined && (
                                            <Badge
                                                variant="secondary"
                                                className={`${item.id === 'active-deposit' || item.id === 'active-withdraw' || item.id === 'appeals'
                                                    ? 'bg-red-600 hover:bg-red-600 text-white font-semibold motion-safe:animate-pulse'
                                                    : 'bg-gray-700 text-gray-300'
                                                    }`}
                                            >
                                                {item.count > 99 ? '99+' : item.count}
                                            </Badge>
                                        )}
                                        {item.badge && (
                                            <Badge variant="default" className="bg-blue-600">
                                                {item.badge}
                                            </Badge>
                                        )}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* User Info - Fixed at bottom using flex */}
            <div className="mt-auto p-4 border-t border-gray-700 bg-gray-800">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                        <Users className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                        <div className="text-sm font-medium">{user?.username || 'trader'}</div>
                        <div className="text-xs text-gray-400">Онлайн</div>
                    </div>

                </div>
            </div>
        </div>
    );
};
